import { FC, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { RewardsDataDTO } from "entities/RewardsDataDTO";
import axiosInstance from "api/AxiosInstance";
import Heading from "components/Heading/Heading";
import Divider from "components/Divider";
import { useDropzone } from "react-dropzone";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import PackingSlipUploader from "./UploadPackingSlip";
import PageLoader from "components/CSCLedLoader/PageLoader";
import ProofOfPurchaseReceived from "./ProofOfPurchaseReceived";
import { config } from "utils/Constance";
import MyAccountRewardsBanner from "./MyAccountRewardsBanner";
import ProofOfPurchaseTable from "./ProofOfPurchaseTable";

export interface MyAccountRewardsProps {
    className?: string;
}

interface RewardsReceiptViewModel {
  id: number;
  receiptNumber: string;
  dateUploaded: string;
  status: string;
  totalPointValue:string;
  uploadReceiptsPath: string;
};

const MyAccountRewards: FC<MyAccountRewardsProps> = ({ className = "" }) => {
     const user = useAppSelector((state: RootState) => state.auth);
     const navigate = useNavigate();
     const [rewardsShopData, setRewardsShopData] = useState<RewardsDataDTO | null>(null);
     const [RewardConfirmOrderId, setRewardConfirmOrderId] = useState<number>(0);
     const isPreviewable = (file: File) => {
     const extensions = [
        "pdf",
        "csv",
        "xls",
        "xlsx",
        "doc",
        "docx",
        "txt",
        "zip",
      ];
      const extension = file.name.split(".").pop()?.toLowerCase();
      return !extensions.includes(extension || "");
    };
     const [UploadbuttonClicked, setUploadButtonClicked] = useState(false);
     const [files, setFiles] = useState<File[]>([]);
     const [errorFile, setErrorFile] = useState("");
     const [note, setNote] = useState("");
     const [rewardsPOPList, setrewardsPOPList] = useState<RewardsReceiptViewModel[] | undefined>();
     const [rewardsId, setRewardsId] = useState(0);
     const [loading, setLoading] = useState(true);
     const [receiptNumber, setReceiptNumber] = useState("");
     const rewardsUpload = useRef<HTMLDivElement>(null);
     const [IsRewardsProgram, setIsRewardsProgram] = useState<boolean>();
    
    const onDrop = (acceptedFiles: File[]) => {
        setFiles(acceptedFiles);
        setErrorFile("");
    };
    
    const handleRemove = (indexToRemove: number) => {
        setFiles((prevFiles) =>
          prevFiles.filter((_, index) => index !== indexToRemove)
        );
        if(indexToRemove == 0){
            setErrorFile(
                "You need to attach a packing slip or invoice before you can submit your proof of purchase."
              );
        }
    };

    const handleNoteChange = (e: any) => {
        setNote(e.target.value);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    useEffect(() => {
        fetchRewardsProgram();
        if(!user.isAuthenticated){
            navigate("/");
        }
        else{
            fetchRewardsShopData();
            fetchrewardsReceiptByUserId();
        }
    }, [user,RewardConfirmOrderId]);

    const fetchRewardsProgram = async () => {
        const response = await axiosInstance
          .get(`/Rewards/RewardProgram`)
          .then((response) => {
            setIsRewardsProgram(response.data);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
          });
    };

    const fetchrewardsReceiptByUserId = async () => {
        if(user.isAuthenticated){
          const response = await axiosInstance
          .get(`/Rewards/GetRewardsReceiptsByUserId?userId=${user.userData?.userId}`)
          .then((response) => {
            setrewardsPOPList(response.data.rewardsReceipts);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
          });
        }
    };

    const fetchRewardsShopData = async () => {
      try {
        const response = await axiosInstance
          .get(
            `/Rewards/rewards-shop?RewardConfirmOrderId=${RewardConfirmOrderId}`
          )
          .then((response) => {
            setRewardsShopData(response.data ?? {});
          });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const fetchGetReceiptNumber = async (Id: any) => {
        const response = await axiosInstance
          .get(`/Rewards/GetRewardsReceiptById?Id=${Id}`)
          .then((response) => {
            setReceiptNumber(response.data);
          })
          .catch((error) => {
            console.error("Error fetching data:", error);
          });
    };

    const handleSubmit = async () => {
        if (!UploadbuttonClicked) {
          if (files.length === 0) {
            setErrorFile(
              "You need to attach a packing slip or invoice before you can submit your proof of purchase."
            );
            return;
          }
          try {
            setUploadButtonClicked(true);
            const formData = new FormData();
            formData.append("file", files[0]);
            const config = {
              headers: {
                "Content-Type": "multipart/form-data",
                accept: "*/*",
              },
              params: {
                CustomerProvidedNote: note,
              },
            };
            const response = await axiosInstance
              .post("/Rewards/AddRewardsReceipts", formData, config)
              .then((response) => {
                setRewardsId(response.data);
                fetchGetReceiptNumber(response.data);
                fetchrewardsReceiptByUserId()
                setTimeout(() => {
                  setLoading(false);
                }, 3000);
              });
          } catch (error) {
            console.error("API Error:", error);
          }
        }
  };

  const handleuploadPOP = () => {
       setUploadButtonClicked(false);
       setLoading(true);
       setFiles([]);
       setRewardsId(0);
       setIsRewardsProgram(true);
       setNote("");
       navigate("/rewards/my-rewards");
  };

return(
    <div className={`nc-MyRewards relative overflow-hidden ${className}`} data-nc-id="MyRewards">
        <Helmet>
            <title>My Rewards Orders | CSC LED Account Dashboard</title>
            <meta name="description" content="Access your CSC LED account to view your rewards program activity. Track submitted proof of purchases and rewards redemptions." />
            <meta name="keywords" content="CSC LED account, order history, LED product orders, rewards program, proof of purchase, loyalty rewards, CSC LED rewards, track orders, lighting purchases, LED rewards, commercial lighting orders, customer account dashboard" />
        </Helmet>
        <div className="w-full">
           <div className="relative overflow-hidden">
              <MyAccountRewardsBanner
                  userPoints={rewardsShopData?.rewardPoint ?? null}
                  isRewardAccess={rewardsShopData?.isRewardAccess ?? null}
              />
            </div>
              <div className="container pt-8 pb-4 sm:pb-16 lg:pb-28 lg:pt-20 space-y-16 sm:space-y-20 lg:space-y-28">
                <div className="space-y-10 lg:space-y-14">
                  <div className="max-w-screen-sm scroll-m-28">
                     <div className="hidden sm:block">
                        <Heading
                          className="text-neutral-900 dark:text-neutral-50"
                          isCenter={false}
                        >
                          My Rewards
                        </Heading>
                        <span className="block mt-4 text-black-500 dark:text-black-400 halloween:text-black text-sm sm:text-base">
                          Everything you need to manage your rewards, in one spot.
                        </span>
                     </div>
                      <div className="block sm:hidden w-full px-4 sm:pt-6 pt-0 text-center">
                         <h1 className="text-xl xs:text-2xl font-semibold text-neutral-900 dark:text-neutral-50"> 
                         My Rewards 
                        </h1>
                         <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400">
                           Everything you need to manage your rewards, in one spot. 
                          </p>
                        </div>
                    </div>
                    <Divider />
                    <div className="!mt-1">
                          {rewardsId == 0 && (
                              <PackingSlipUploader
                                  files={files}
                                  isDragActive={isDragActive}
                                  getRootProps={getRootProps}
                                  getInputProps={getInputProps}
                                  isPreviewable={isPreviewable}
                                  handleRemove={handleRemove}
                                  errorFile={errorFile}
                                  note={note}
                                  handleNoteChange={handleNoteChange}
                                  handleSubmit={handleSubmit}
                                  UploadbuttonClicked={UploadbuttonClicked}
                              />
                          )}
                          {user.isAuthenticated &&  rewardsId !== 0 && (
                              loading ? (
                                  <PageLoader />
                              ) : (
                                  <ProofOfPurchaseReceived
                                  receiptNumber={receiptNumber}
                                  handleUploadPOP={handleuploadPOP}
                                  emailMarketing={config.emails.EmailMarketing}
                                  rewardsUpload={rewardsUpload}
                                  />
                              )
                          )}
                          <div className="!mt-5">
                              <ProofOfPurchaseTable rewardsData={rewardsPOPList ?? []} />
                          </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}

export default MyAccountRewards;